import { useEffect, useState } from "react";
import { _get } from "../../../libraries/fetch";
import { copyToClipboard, showToast } from "../../../libraries/clipboardUtils";
import { getUsersByBranchURL } from "../../../libraries/apiUrl";

function UsersInfo({ branchId, companyId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedAdminId, setCopiedAdminId] = useState(null);
  const [copiedOperatorId, setCopiedOperatorId] = useState(null);
  const [copiedTrainerId, setCopiedTrainerId] = useState(null);
  const [copiedNameId, setCopiedNameId] = useState(null);
  const [copiedEmailId, setCopiedEmailId] = useState(null);
  const [copiedPasswordId, setCopiedPasswordId] = useState(null);
  const [copiedSuperAdminEmailId, setCopiedSuperAdminEmailId] = useState(null);
  const [copiedSuperAdminPasswordId, setCopiedSuperAdminPasswordId] = useState(null);
  const [error, setError] = useState(null);

  // Helper function to get role name for filtering
  const getUserRoleName = (user) => {
    // Check designation field directly
    if (user.designation) {
      return user.designation;
    }

    // If role is a string, return it directly
    if (user.role && typeof user.role === 'string') {
      return user.role;
    }

    // If role is an object, get the name
    if (user.role && typeof user.role === 'object') {
      return user.role.name || user.role.roleName || "";
    }

    // Check roleName field
    if (user.roleName) {
      return user.roleName;
    }

    return "";
  };

  useEffect(() => {
    if (!branchId) {
      setLoading(false);
      setError("No branch ID provided");
      return;
    }

    setLoading(true);
    setError(null);
    setUsers([]);

    _get(getUsersByBranchURL(branchId), { useCache: false })
      .then(usersData => {
        // Process users - handle various response structures
        let usersList = [];
        if (usersData) {
          if (Array.isArray(usersData)) {
            usersList = usersData;
          } else if (usersData.response && Array.isArray(usersData.response)) {
            usersList = usersData.response;
          } else if (usersData.data && Array.isArray(usersData.data)) {
            usersList = usersData.data;
          } else if (usersData.users && Array.isArray(usersData.users)) {
            usersList = usersData.users;
          } else if (usersData.result && Array.isArray(usersData.result)) {
            usersList = usersData.result;
          } else if (usersData._id || usersData.id || usersData.email) {
            // Single user object
            usersList = [usersData];
          }
        }

        // Filter users to only show Admin, Operator, and Trainer roles
        const allowedRoles = ['admin', 'operator', 'trainer'];
        const filteredUsers = usersList.filter(user => {
          const roleName = getUserRoleName(user);
          return allowedRoles.includes(roleName.toLowerCase());
        });

        setUsers(filteredUsers);
        setLoading(false);
        setError(null);
      })
      .catch(err => {
        setLoading(false);
        setUsers([]);
        setError("Failed to load users. Please try again.");
      });
  }, [branchId]);

  // Helper function to get designation for a user
  const getUserDesignation = (user) => {
    // Check designation field directly
    if (user.designation) {
      return user.designation;
    }

    // If role is a string, return it directly
    if (user.role && typeof user.role === 'string') {
      return user.role;
    }

    // If role is an object, get the name
    if (user.role && typeof user.role === 'object') {
      return user.role.name || user.role.roleName || "N/A";
    }

    // Check roleName field
    if (user.roleName) {
      return user.roleName;
    }

    return "N/A";
  };

  async function handleCopyAdminInfo(user) {
    const designationValue = getUserDesignation(user);
    const contactNumber = user.phone || user.contactNumber || user.phoneNumber || user.mobile || "N/A";
    const userInfo = `name : ${user.name || user.userName || user.fullName || "N/A"}\nemail : ${user.email || "N/A"}\npassword : ${user.password || "N/A"}\ndesignation : ${designationValue}\ncontact number : ${contactNumber}`;
    const result = await copyToClipboard(userInfo, { label: "Admin user credentials" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedAdminId(user._id || user.id);
      setTimeout(() => {
        setCopiedAdminId(null);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopyOperatorInfo(user) {
    const designationValue = getUserDesignation(user);
    const contactNumber = user.phone || user.contactNumber || user.phoneNumber || user.mobile || "N/A";
    const userInfo = `name : ${user.name || user.userName || user.fullName || "N/A"}\nemail : ${user.email || "N/A"}\npassword : ${user.password || "N/A"}\ndesignation : ${designationValue}\ncontact number : ${contactNumber}`;
    const result = await copyToClipboard(userInfo, { label: "Operator user credentials" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedOperatorId(user._id || user.id);
      setTimeout(() => {
        setCopiedOperatorId(null);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopyTrainerInfo(user) {
    const designationValue = getUserDesignation(user);
    const contactNumber = user.phone || user.contactNumber || user.phoneNumber || user.mobile || "N/A";
    const userInfo = `name : ${user.name || user.userName || user.fullName || "N/A"}\nemail : ${user.email || "N/A"}\npassword : ${user.password || "N/A"}\ndesignation : ${designationValue}\ncontact number : ${contactNumber}`;
    const result = await copyToClipboard(userInfo, { label: "Trainer user credentials" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedTrainerId(user._id || user.id);
      setTimeout(() => {
        setCopiedTrainerId(null);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopyEmailPassword(user) {
    const emailPassword = `email : ${user.email || "N/A"}\npassword : ${user.password || "N/A"}`;
    const result = await copyToClipboard(emailPassword, { label: "User email and password" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedNameId(user._id || user.id);
      setTimeout(() => {
        setCopiedNameId(null);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopyEmail(user, e) {
    if (e) {
      e.stopPropagation();
    }
    const email = user.email || "N/A";
    const result = await copyToClipboard(email, { label: "User email" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedEmailId(user._id || user.id);
      setTimeout(() => {
        setCopiedEmailId(null);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopyPassword(user, e) {
    if (e) {
      e.stopPropagation();
    }
    const password = user.password || "N/A";
    const result = await copyToClipboard(password, { label: "User password" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedPasswordId(user._id || user.id);
      setTimeout(() => {
        setCopiedPasswordId(null);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopySuperAdminEmail(e) {
    if (e) {
      e.stopPropagation();
    }
    const email = "admin@tecfolk.com";
    const result = await copyToClipboard(email, { label: "Super admin email" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedSuperAdminEmailId('super-admin-email');
      setTimeout(() => {
        setCopiedSuperAdminEmailId(null);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopySuperAdminPassword(e) {
    if (e) {
      e.stopPropagation();
    }
    const password = "Storm@562";
    const result = await copyToClipboard(password, { label: "Super admin password" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedSuperAdminPasswordId('super-admin-password');
      setTimeout(() => {
        setCopiedSuperAdminPasswordId(null);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopyAllNames(userList, roleLabel) {
    const names = userList
      .map((user) => user.name || user.userName || user.fullName || "N/A")
      .filter((name) => name && name !== "N/A");

    if (names.length === 0) {
      showToast(`No ${roleLabel.toLowerCase()} names found to copy.`, 'warning');
      return;
    }

    const result = await copyToClipboard(names.join("\n"), { label: `${roleLabel} names` });
    if (result.success) {
      showToast(`${roleLabel} names copied to clipboard!`, 'success');
    } else {
      showToast(result.message, 'error');
    }
  }

  // Helper function to group users by role
  const groupUsersByRole = () => {
    const admins = [];
    const operators = [];
    const trainers = [];

    users.forEach(user => {
      const roleName = getUserRoleName(user).toLowerCase();
      if (roleName === 'admin' || roleName === 'administrator') {
        admins.push(user);
      } else if (roleName === 'operator') {
        operators.push(user);
      } else if (roleName === 'trainer') {
        trainers.push(user);
      }
    });

    return { admins, operators, trainers };
  };

  const renderUserCards = (userList, onCopy, copiedId, emptyMessage = "No users found", showWhatsApp = false) => {
    if (userList.length === 0) {
      return <p style={{ padding: '20px', color: '#888' }}>{emptyMessage}</p>;
    }

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
        gap: '20px',
        padding: '20px'
      }}>
        {userList.map((user, index) => (
          <div
            key={user._id || user.id || index}
            className="user-card"
            onClick={() => onCopy(user)}
            style={{
              backgroundColor: '#2a2d35',
              border: '1px solid #444',
              borderRadius: '12px',
              padding: '20px',
              cursor: 'pointer',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#33363f';
              e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.4), 0 0 20px rgba(24, 119, 24, 0.3)';
              e.currentTarget.style.border = '1px solid #187718';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2a2d35';
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
              e.currentTarget.style.border = '1px solid #444';
            }}
          >
            <div style={{ marginBottom: '15px' }}>
              <h3
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyEmailPassword(user);
                }}
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#fff',
                  margin: '0 0 10px 0',
                  borderBottom: '2px solid #187718',
                  paddingBottom: '8px',
                  cursor: 'pointer',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#4ade80';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#fff';
                }}
              >
                {user.name || user.userName || user.fullName || "N/A"}
              </h3>
            </div>

            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '15px', color: '#888' }}>Designation</span>
              <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600' }}>{getUserDesignation(user)}</span>
            </div>

            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '15px', color: '#888' }}>ID</span>
              <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600', wordBreak: 'break-all' }}>
                {user.userCode || "N/A"}
              </span>
            </div>

            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '15px', color: '#888' }}>Email</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600', wordBreak: 'break-word' }}>
                  {user.email || "N/A"}
                </span>
                <button
                  onClick={(e) => handleCopyEmail(user, e)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s',
                    position: 'relative'
                  }}
                  title="Copy email"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(24, 119, 24, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {copiedEmailId === (user._id || user.id) ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="#187718">
                      <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="#888">
                      <path d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z" />
                    </svg>
                  )}
                  {copiedEmailId === (user._id || user.id) && (
                    <div style={{
                      position: 'absolute',
                      top: '-35px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'linear-gradient(135deg, #187718 0%, #20a020 100%)',
                      color: '#fff',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      zIndex: 1001,
                      boxShadow: '0 4px 12px rgba(24, 119, 24, 0.4), 0 2px 4px rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      pointerEvents: 'none'
                    }}>
                      Email Copied!
                    </div>
                  )}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '15px', color: '#888' }}>Password</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600', fontFamily: 'monospace' }}>
                  {user.password || "N/A"}
                </span>
                <button
                  onClick={(e) => handleCopyPassword(user, e)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s',
                    position: 'relative'
                  }}
                  title="Copy password"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(24, 119, 24, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {copiedPasswordId === (user._id || user.id) ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="#187718">
                      <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="#888">
                      <path d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z" />
                    </svg>
                  )}
                  {copiedPasswordId === (user._id || user.id) && (
                    <div style={{
                      position: 'absolute',
                      top: '-35px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'linear-gradient(135deg, #187718 0%, #20a020 100%)',
                      color: '#fff',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      zIndex: 1001,
                      boxShadow: '0 4px 12px rgba(24, 119, 24, 0.4), 0 2px 4px rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      pointerEvents: 'none'
                    }}>
                      Password Copied!
                    </div>
                  )}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '15px', color: '#888' }}>Contact Number</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600' }}>
                  {user.phone || user.contactNumber || user.phoneNumber || user.mobile || "N/A"}
                </span>
                {showWhatsApp && (user.phone || user.contactNumber || user.phoneNumber || user.mobile) && (
                  <a
                    href={`https://wa.me/${(user.phone || user.contactNumber || user.phoneNumber || user.mobile).replace(/\D/g, '')}?text=Dear Client`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(37, 211, 102, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Open WhatsApp"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </a>
                )}
              </div>
            </div>

            {copiedId === (user._id || user.id) && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'linear-gradient(135deg, #187718 0%, #20a020 100%)',
                color: '#fff',
                padding: '10px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(24, 119, 24, 0.4), 0 2px 4px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                animation: 'slideInBounce 0.4s ease-out',
                border: '1px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                transform: 'scale(1)',
                letterSpacing: '0.3px'
              }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  width="16"
                  height="16"
                  fill="currentColor"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                </svg>
                <span>Details Copied!</span>
              </div>
            )}
            {copiedNameId === (user._id || user.id) && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'linear-gradient(135deg, #187718 0%, #20a020 100%)',
                color: '#fff',
                padding: '10px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(24, 119, 24, 0.4), 0 2px 4px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                animation: 'slideInBounce 0.4s ease-out',
                border: '1px solid rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                transform: 'scale(1)',
                letterSpacing: '0.3px'
              }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  width="16"
                  height="16"
                  fill="currentColor"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                </svg>
                <span>Email & Password Copied!</span>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes slideInBounce {
          0% {
            opacity: 0;
            transform: translateX(20px) scale(0.8);
          }
          50% {
            transform: translateX(-5px) scale(1.05);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
      `}</style>
      <div className="configMain">
        <h1>Users Information</h1>

        {/* Super Admin Section */}
        <div style={{ marginBottom: '40px', marginTop: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '15px',
            color: '#fff',
            paddingBottom: '10px',
            borderBottom: '2px solid #187718'
          }}>
            Super Admin
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
            gap: '20px',
            padding: '20px'
          }}>
            <div
              style={{
                backgroundColor: '#2a2d35',
                border: '1px solid #444',
                borderRadius: '12px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#33363f';
                e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.4), 0 0 20px rgba(24, 119, 24, 0.3)';
                e.currentTarget.style.border = '1px solid #187718';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2d35';
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
                e.currentTarget.style.border = '1px solid #444';
              }}
            >
              <div style={{ marginBottom: '15px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#fff',
                  margin: '0 0 10px 0',
                  borderBottom: '2px solid #187718',
                  paddingBottom: '8px'
                }}>
                  Super Admin Credentials
                </h3>
              </div>

              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: '#888' }}>Email</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600', wordBreak: 'break-word' }}>
                    admin@tecfolk.com
                  </span>
                  <button
                    onClick={(e) => handleCopySuperAdminEmail(e)}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      position: 'relative'
                    }}
                    title="Copy email"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(24, 119, 24, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="18" height="18" fill="#187718">
                      <path d="M272 0H396.1c12.7 0 24.9 5.1 33.9 14.1l67.9 67.9c9 9 14.1 21.2 14.1 33.9V336c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V48C0 21.5 21.5 0 48 0H272zm-60 165.1V56H48c-8.8 0-16 7.2-16 16V336c0 8.8 7.2 16 16 16H416c8.8 0 16-7.2 16-16V150.9H372c-13.3 0-24-10.7-24-24V76z"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: '#888' }}>Password</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px', color: '#fff', fontWeight: '600', wordBreak: 'break-word' }}>
                    Storm@562
                  </span>
                  <button
                    onClick={(e) => handleCopySuperAdminPassword(e)}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      position: 'relative'
                    }}
                    title="Copy password"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(24, 119, 24, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="18" height="18" fill="#187718">
                      <path d="M272 0H396.1c12.7 0 24.9 5.1 33.9 14.1l67.9 67.9c9 9 14.1 21.2 14.1 33.9V336c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V48C0 21.5 21.5 0 48 0H272zm-60 165.1V56H48c-8.8 0-16 7.2-16 16V336c0 8.8 7.2 16 16 16H416c8.8 0 16-7.2 16-16V150.9H372c-13.3 0-24-10.7-24-24V76z"/>
                    </svg>
                  </button>
                </div>
              </div>

              {copiedSuperAdminEmailId === 'super-admin-email' && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'linear-gradient(135deg, #187718 0%, #20a020 100%)',
                  color: '#fff',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(24, 119, 24, 0.4), 0 2px 4px rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  animation: 'slideInBounce 0.4s ease-out',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  transform: 'scale(1)',
                  letterSpacing: '0.3px'
                }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    width="16"
                    height="16"
                    fill="currentColor"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                  </svg>
                  <span>Email Copied!</span>
                </div>
              )}
              {copiedSuperAdminPasswordId === 'super-admin-password' && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'linear-gradient(135deg, #187718 0%, #20a020 100%)',
                  color: '#fff',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(24, 119, 24, 0.4), 0 2px 4px rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  animation: 'slideInBounce 0.4s ease-out',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  transform: 'scale(1)',
                  letterSpacing: '0.3px'
                }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    width="16"
                    height="16"
                    fill="currentColor"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                  </svg>
                  <span>Password Copied!</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <p style={{ padding: '20px', color: '#fff' }}>Loading users...</p>
        ) : error ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p style={{ color: '#ff6b6b', fontSize: '16px', marginBottom: '10px' }}>⚠️ {error}</p>
            <p style={{ color: '#888', fontSize: '14px' }}>This branch may not have any users assigned yet.</p>
          </div>
        ) : (
          <div style={{ marginTop: '20px' }}>
            {(() => {
              const { admins, operators, trainers } = groupUsersByRole();
              return (
                <>
                  {/* Admin Section */}
                  <div style={{ marginBottom: '40px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '2px solid #187718'
                    }}>
                      <h2 style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#fff',
                        margin: 0
                      }}>
                        Admin ({admins.length})
                      </h2>
                      <button
                        onClick={() => handleCopyAllNames(admins, 'Admins')}
                        style={{
                          backgroundColor: '#187718',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 14px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease, transform 0.2s ease',
                          boxShadow: '0 4px 10px rgba(24, 119, 24, 0.25)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#1f8f1f';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#187718';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        Copy All Names
                      </button>
                    </div>
                    {renderUserCards(admins, handleCopyAdminInfo, copiedAdminId, "No admins found", true)}
                  </div>

                  {/* Operators Section */}
                  <div style={{ marginBottom: '40px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '2px solid #187718'
                    }}>
                      <h2 style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#fff',
                        margin: 0
                      }}>
                        Operators ({operators.length})
                      </h2>
                      <button
                        onClick={() => handleCopyAllNames(operators, 'Operators')}
                        style={{
                          backgroundColor: '#187718',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 14px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease, transform 0.2s ease',
                          boxShadow: '0 4px 10px rgba(24, 119, 24, 0.25)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#1f8f1f';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#187718';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        Copy All Names
                      </button>
                    </div>
                    {renderUserCards(operators, handleCopyOperatorInfo, copiedOperatorId, "No operators found")}
                  </div>

                  {/* Trainers Section */}
                  <div style={{ marginBottom: '40px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '2px solid #187718'
                    }}>
                      <h2 style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#fff',
                        margin: 0
                      }}>
                        Trainers ({trainers.length})
                      </h2>
                      <button
                        onClick={() => handleCopyAllNames(trainers, 'Trainers')}
                        style={{
                          backgroundColor: '#187718',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 14px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease, transform 0.2s ease',
                          boxShadow: '0 4px 10px rgba(24, 119, 24, 0.25)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#1f8f1f';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#187718';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        Copy All Names
                      </button>
                    </div>
                    {renderUserCards(trainers, handleCopyTrainerInfo, copiedTrainerId, "No trainers found")}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </>
  );
}

export default UsersInfo;

